/**
 * ComponentRenderer
 *
 * Renders preview content for each label component type.
 * Uses sample data for realistic preview in the editor.
 *
 * Supports all 14 component types:
 * - qr, barcode, orderNumberBarcode, orderNumber, sizeColor, productInfo
 * - itemCount, binLabel, productType, printArea
 * - checkboxes, dtgIndicator, ptBadge, redoBadge
 *
 * Features:
 * - Font sizes scale with component dimensions
 * - Conditional badges show placeholder when condition not met
 * - Uses same colors from label-constants.ts for consistency
 * - Checkboxes adapt layout based on aspect ratio
 * - React.memo for performance
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import type { ComponentType, SampleGarmentData } from './label-layout-types';
import { LABEL_COLORS } from '@/features/production-orders/utils/label-constants';
import { printAreaFontSize } from '@/features/production-orders/utils/print-area-fit';
import {
  isValidBarcodeContent,
  sanitizeBarcodeContent,
} from '@/features/production-orders/utils/barcode-config';
import { formatOrderNumber } from '@threadheads/shared';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface ComponentRendererProps {
  type: ComponentType;
  data: SampleGarmentData;
  width: number;  // pixels
  height: number; // pixels
}

// ============================================================================
// COMPONENT
// ============================================================================

function ComponentRendererInner({ type, data, width, height }: ComponentRendererProps) {
  const { t } = useTranslation('settings');
  // Scale font sizes based on component height
  const baseFontSize = Math.max(10, Math.min(14, height * 0.3));
  const smallFontSize = Math.max(9, baseFontSize * 0.8);

  switch (type) {
    case 'qr':
      return (
        <div className="w-full h-full flex items-center justify-center bg-white p-1">
          <QRCodeSVG
            value={`THP://garment/${data.id}/${data.orderNumber}/${data.sku}`}
            size={Math.min(width, height) * 0.9}
            level="M"
            includeMargin={false}
          />
        </div>
      );

    case 'barcode': {
      // Calculate barcode dimensions to fit container
      const barcodeWidth = Math.max(1, width / 80);
      const barcodeHeight = Math.max(20, height * 0.6);
      // Validate and sanitize barcode content
      const barcodeValue = isValidBarcodeContent(data.sku)
        ? data.sku
        : sanitizeBarcodeContent(data.sku);
      return (
        <div className="w-full h-full flex items-center justify-center bg-white overflow-hidden">
          <Barcode
            value={barcodeValue}
            format="CODE128"
            width={barcodeWidth}
            height={barcodeHeight}
            fontSize={Math.max(6, height * 0.15)}
            margin={0}
            displayValue={true}
            background="#ffffff"
            lineColor="#000000"
          />
        </div>
      );
    }

    case 'orderNumberBarcode': {
      // Order number barcode - CODE128 encoding the order number for shipping scan
      if (!data.orderNumber) {
        return (
          <div className="w-full h-full bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
            {t('production.labelContent.noOrderNumber', 'No order number')}
          </div>
        );
      }
      const orderBarcodeWidth = Math.max(1, width / 80);
      const orderBarcodeHeight = Math.max(20, height * 0.6);
      const orderBarcodeValue = isValidBarcodeContent(data.orderNumber)
        ? data.orderNumber
        : sanitizeBarcodeContent(data.orderNumber);
      return (
        <div className="w-full h-full flex items-center justify-center bg-white overflow-hidden">
          <Barcode
            value={orderBarcodeValue}
            format="CODE128"
            width={orderBarcodeWidth}
            height={orderBarcodeHeight}
            fontSize={Math.max(6, height * 0.15)}
            margin={0}
            displayValue={true}
            background="#ffffff"
            lineColor="#000000"
          />
        </div>
      );
    }

    case 'orderNumber':
      return (
        <div
          className="w-full h-full flex items-center justify-center bg-white border border-black font-bold"
          style={{ fontSize: baseFontSize }}
        >
          #{formatOrderNumber(data.orderNumber)}
        </div>
      );

    case 'sizeColor':
      return (
        <div
          className="w-full h-full flex items-center justify-center bg-white border border-black font-bold"
          style={{ fontSize: baseFontSize }}
        >
          <span className="truncate px-1">
            {data.size} | {data.color.toUpperCase()}
          </span>
        </div>
      );

    case 'productInfo':
      return (
        <div
          className="w-full h-full bg-white border border-black p-1 overflow-hidden"
          style={{ fontSize: smallFontSize }}
        >
          <div className="font-bold overflow-hidden break-words leading-tight" title={data.productName}>
            {data.productName}
          </div>
          <div className="text-gray-600 truncate">{data.sku}</div>
        </div>
      );

    case 'itemCount':
      return (
        <div
          className="w-full h-full flex items-center justify-center bg-white border border-black"
          style={{ fontSize: smallFontSize }}
        >
          {t('production.labelContent.itemOf', { item: data.itemNumber, total: data.totalItems })}
        </div>
      );

    case 'binLabel':
      return data.binLabel ? (
        <div
          className="w-full h-full flex items-center justify-center bg-white border border-black font-bold"
          style={{ fontSize: baseFontSize }}
        >
          <span className="truncate px-1">&rarr; {data.binLabel}</span>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 text-gray-400 text-xs">
          {t('production.labelContent.noBinAssigned')}
        </div>
      );

    case 'productType':
      return data.shopifyProductType ? (
        <div
          className="w-full h-full flex items-center justify-center bg-white border border-black"
          style={{ fontSize: smallFontSize }}
        >
          <span className="truncate px-1">{data.shopifyProductType}</span>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 text-gray-400 text-xs">
          {t('production.labelContent.noProductType')}
        </div>
      );

    case 'printArea': {
      // Phase 104 (D-09): where each print goes on a manual order's garment.
      // Uppercase English on purpose: the printed label is not translated.
      const lines = data.printArea ?? [];
      if (lines.length === 0) {
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 text-gray-400 text-xs text-center px-1">
            {t('production.labelContent.noPrintArea')}
          </div>
        );
      }
      // The same fit as the printed label (GarmentLabel): shrink, then wrap, never cut short.
      const lineFontSize = printAreaFontSize({ lines, width, height, base: baseFontSize, min: 7 });
      return (
        <div
          className="w-full h-full flex flex-col justify-center overflow-hidden bg-white border border-black font-bold leading-tight px-1"
          style={{ fontSize: lineFontSize }}
        >
          {lines.map((line) => (
            <span key={line} className="whitespace-normal [overflow-wrap:anywhere]">
              {line}
            </span>
          ))}
        </div>
      );
    }

    case 'checkboxes': {
      // Determine layout based on aspect ratio
      const isHorizontal = width > height * 1.5;
      return (
        <div
          className={cn(
            'w-full h-full flex bg-white',
            isHorizontal ? 'flex-row gap-0.5' : 'flex-col gap-0.5'
          )}
        >
          {(['front', 'back'] as const).map((side) => (
            <div
              key={side}
              className="flex-1 border border-black flex items-center justify-center font-bold"
              style={{ fontSize: baseFontSize }}
            >
              {t(`production.labelContent.${side}`)}
            </div>
          ))}
        </div>
      );
    }

    case 'dtgIndicator':
      return (
        <div
          className="w-full h-full flex items-center justify-center font-bold text-white"
          style={{
            fontSize: smallFontSize,
            backgroundColor: LABEL_COLORS.BORDER,
          }}
        >
          DTG
        </div>
      );

    case 'ptBadge':
      return data.requiresPretreat ? (
        <div
          className="w-full h-full flex items-center justify-center font-bold"
          style={{
            fontSize: smallFontSize,
            backgroundColor: LABEL_COLORS.PRETREAT_BADGE_BG,
            color: LABEL_COLORS.PRETREAT_BADGE_TEXT,
          }}
        >
          PT
        </div>
      ) : (
        <div className="w-full h-full bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
          {t('production.labelContent.ptHidden')}
        </div>
      );

    case 'redoBadge':
      return data.isRedo ? (
        <div
          className="w-full h-full flex items-center justify-center font-bold text-white"
          style={{
            fontSize: smallFontSize,
            backgroundColor: LABEL_COLORS.REDO_BADGE,
          }}
        >
          REDO
        </div>
      ) : (
        <div className="w-full h-full bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
          {t('production.labelContent.redoHidden')}
        </div>
      );

    default: {
      // Exhaustive check - TypeScript will error if we miss a case
      const _exhaustiveCheck: never = type;
      return (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 border border-dashed">
          {String(_exhaustiveCheck)}
        </div>
      );
    }
  }
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * React.memo for performance optimization.
 * Only re-renders when type, data, or dimensions change.
 */
export const ComponentRenderer = React.memo(ComponentRendererInner);

ComponentRenderer.displayName = 'ComponentRenderer';
